import { createContext, useContext, type ReactNode } from 'react';
import { useDialogState } from '@/hooks/useDialogState';

type DialogStateValue = ReturnType<typeof useDialogState>;

const DialogContext = createContext<DialogStateValue | null>(null);

/**
 * Provider that owns all dialog open/close flags + edit-targets via
 * `useDialogState`. Lifts the dlg-bag out of Index.tsx so dialogs,
 * shells, and topBarHandlers can read/write it directly through
 * `useDialogs()` instead of receiving 28 dlg props each.
 *
 * Takes `initialWizardOpen` so wizard auto-opens on first mount when
 * the user is not in view-only mode (preserves existing behavior).
 */
export function DialogProvider({
  initialWizardOpen,
  children,
}: {
  initialWizardOpen: boolean;
  children: ReactNode;
}) {
  const dlg = useDialogState(initialWizardOpen);
  return <DialogContext.Provider value={dlg}>{children}</DialogContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDialogs(): DialogStateValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialogs must be used within a <DialogProvider>');
  }
  return ctx;
}
